import { useState } from 'react';
import {
  Button,
  Circle,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  FormErrorMessage,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useForm } from 'react-hook-form';
import { CheckIcon, X } from 'lucide-react';
import type { USIResponse } from '@duke-hq/usi/types';

interface FormData {
  bypass_verification: boolean;
  universal_student_identifier: string;
  first_name: string;
  last_name?: string;
  date_of_birth?: Date;
  use_single_name: boolean;
}

interface USIProps {
  universal_student_identifier: string;
  first_name: string;
  last_name?: string;
  date_of_birth?: Date | null;
  onVerifyUSI: (data: any) => Promise<void>;
  verifyUSIResponse: USIResponse | null | undefined;
  isUSIVerified: boolean;
}

export default function VerifyUsiForm({
  universal_student_identifier,
  first_name,
  last_name,
  date_of_birth,
  onVerifyUSI,
  verifyUSIResponse,
  isUSIVerified,
}: USIProps) {
  const methods = useForm<FormData>();
  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
  } = methods;
  const [showBypassVerificationText, setShowBypassVerificationText] = useState(false);

  return (
    <>
      <Flex align={'center'} justify={'center'} alignItems="center" mb={4}>
        <Text textStyle="2xl" color="fg.default" fontWeight="semibold">
          {<Trans>Student USI</Trans>}
        </Text>
      </Flex>

      <form onSubmit={handleSubmit(onVerifyUSI)}>
        <VStack spacing={2} align="stretch">
          <FormControl isInvalid={!!errors.universal_student_identifier}>
            <FormLabel htmlFor="universal_student_identifier">
              <Trans>USI</Trans>
            </FormLabel>
            <InputGroup>
              {verifyUSIResponse !== undefined && (
                <InputRightElement pointerEvents="none">
                  {verifyUSIResponse?.USIStatus === 'Valid' ? (
                    <Circle bg="success" size="4">
                      <Icon as={CheckIcon} boxSize="2.5" color="fg.inverted" />
                    </Circle>
                  ) : (
                    <Circle bg="danger" size="4">
                      <Icon as={X} boxSize="2.5" color="fg.inverted" />
                    </Circle>
                  )}
                </InputRightElement>
              )}
              <Input
                type="text"
                id="universal_student_identifier"
                defaultValue={universal_student_identifier ?? ''}
                readOnly={true}
                textTransform="uppercase"
                opacity={0.7}
                backgroundColor={'gray.50'}
                {...register('universal_student_identifier', {
                  pattern: {
                    value: /^[A-Za-z0-9]{10}$/,
                    message: t`Unique Student Identifier must be exactly 10 letters or numbers`,
                  },
                  setValueAs: (value) =>
                    typeof value === 'string' ? value.trim().toUpperCase() : '',
                })}
              />
            </InputGroup>
            <FormErrorMessage>{errors.universal_student_identifier?.message}</FormErrorMessage>
            {verifyUSIResponse !== undefined && verifyUSIResponse?.USIStatus !== 'Valid' && (
              <FormHelperText textAlign="center">
                {verifyUSIResponse?.USIStatus === 'Invalid' ? (
                  <Trans>USI is invalid</Trans>
                ) : (
                  <Trans>
                    We could not verify the USI at this point. You can try again now or at a later
                    stage.
                  </Trans>
                )}
              </FormHelperText>
            )}
          </FormControl>
          {verifyUSIResponse !== undefined && (
            <>
              {verifyUSIResponse?.USIStatus === 'Valid' && (
                <>
                  <FormControl isInvalid={!!errors.first_name}>
                    <FormLabel htmlFor="first_name">
                      {verifyUSIResponse?.SingleName ? (
                        <Trans>Single name</Trans>
                      ) : (
                        <Trans>First name</Trans>
                      )}
                    </FormLabel>
                    <InputGroup>
                      <InputRightElement pointerEvents="none">
                        {verifyUSIResponse?.FirstName === 'Match' ||
                        verifyUSIResponse?.SingleName === 'Match' ? (
                          <Circle bg="success" size="4">
                            <Icon as={CheckIcon} boxSize="2.5" color="fg.inverted" />
                          </Circle>
                        ) : (
                          <Circle bg="danger" size="4">
                            <Icon as={X} boxSize="2.5" color="fg.inverted" />
                          </Circle>
                        )}
                      </InputRightElement>
                      <Input
                        type="text"
                        id="first_name"
                        readOnly={true}
                        opacity={0.7}
                        backgroundColor={'gray.50'}
                        {...register('first_name', {
                          required: t`Please enter your first name`,
                          validate: (value) => {
                            const expected = first_name?.trim().toLowerCase();
                            const actual = value?.trim().toLowerCase();
                            if (!expected) {
                              return true;
                            }

                            return (
                              actual === expected || t`First name does not match the one on record`
                            );
                          },
                        })}
                        defaultValue={first_name}
                      />
                    </InputGroup>
                    <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
                  </FormControl>

                  {!verifyUSIResponse?.SingleName ? (
                    <FormControl isInvalid={!!errors.last_name}>
                      <FormLabel htmlFor="last_name">
                        <Trans>Last name</Trans>
                      </FormLabel>
                      <InputGroup>
                        <InputRightElement pointerEvents="none">
                          {verifyUSIResponse?.FamilyName === 'Match' ? (
                            <Circle bg="success" size="4">
                              <Icon as={CheckIcon} boxSize="2.5" color="fg.inverted" />
                            </Circle>
                          ) : (
                            <Circle bg="danger" size="4">
                              <Icon as={X} boxSize="2.5" color="fg.inverted" />
                            </Circle>
                          )}
                        </InputRightElement>
                        <Input
                          type="text"
                          id="last_name"
                          readOnly={true}
                          opacity={0.7}
                          backgroundColor={'gray.50'}
                          {...register('last_name', {
                            required: t`Please enter your last name`,
                            validate: (value) => {
                              const expected = last_name?.trim().toLowerCase();
                              const actual = value?.trim().toLowerCase();
                              if (expected == null || expected.trim() === '') {
                                return true;
                              }

                              return (
                                actual === expected || t`Last name does not match the one on record`
                              );
                            },
                          })}
                          defaultValue={last_name ?? ''}
                        />
                      </InputGroup>
                      <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
                    </FormControl>
                  ) : null}

                  <FormControl isInvalid={!!errors.date_of_birth}>
                    <FormLabel htmlFor="date_of_birth">
                      <Trans>Date of birth</Trans>
                    </FormLabel>
                    <InputGroup>
                      <InputRightElement pointerEvents="none">
                        {verifyUSIResponse?.DateOfBirth === 'Match' ? (
                          <Circle bg="success" size="4">
                            <Icon as={CheckIcon} boxSize="2.5" color="fg.inverted" />
                          </Circle>
                        ) : (
                          <Circle bg="danger" size="4">
                            <Icon as={X} boxSize="2.5" color="fg.inverted" />
                          </Circle>
                        )}
                      </InputRightElement>
                      <Input
                        type="date"
                        id="date_of_birth"
                        readOnly={true}
                        opacity={0.7}
                        backgroundColor={'gray.50'}
                        {...register('date_of_birth', {
                          required: t`Please enter your date of birth`,
                          valueAsDate: true,
                          validate: (value) => {
                            const expected = date_of_birth;
                            if (!expected || !(expected instanceof Date)) {
                              return true;
                            }

                            const expectedStr = expected.toISOString().substring(0, 10);
                            const actualStr = value?.toISOString().substring(0, 10);

                            return (
                              actualStr === expectedStr ||
                              t`Date of birth does not match the one on record`
                            );
                          },
                        })}
                        defaultValue={date_of_birth?.toISOString().substring(0, 10) ?? ''}
                      />
                    </InputGroup>
                    <FormErrorMessage>{errors.date_of_birth?.message}</FormErrorMessage>
                  </FormControl>
                </>
              )}

              <HStack align={'center'} justify={'center'} m={4}>
                <Circle bg={isUSIVerified ? 'success' : 'danger'} size="5">
                  <Icon as={isUSIVerified ? CheckIcon : X} boxSize="3.5" color="fg.inverted" />
                </Circle>
                <Text color="fg.emphasis" textStyle="sm" fontWeight="bold">
                  {isUSIVerified ? <Trans>Verified</Trans> : <Trans>Could not verify</Trans>}
                </Text>
              </HStack>
            </>
          )}

          <VStack align={'left'} mt={4} mb={2}>
            <FormControl>
              <CheckboxGroup>
                <Checkbox id="use_single_name" {...register('use_single_name')}>
                  Use Single Name
                </Checkbox>
              </CheckboxGroup>
            </FormControl>
          </VStack>

          <VStack align={'left'} mt={4} mb={2}>
            <FormControl>
              <CheckboxGroup>
                <Checkbox
                  id="bypass_verification"
                  {...register('bypass_verification')}
                  onChange={(e) => {
                    setShowBypassVerificationText(e.target.checked);
                  }}
                >
                  Bypass USI verification
                </Checkbox>
                {showBypassVerificationText ? (
                  <FormHelperText>
                    <Trans>
                      Bypassing the verification will verify the USI and add{' '}
                      <b>
                        <i>#_</i>
                      </b>{' '}
                      prefix to the USI stored in our system. You can re-verify the students USI
                      outside of our system and update the students profile accordingly.
                    </Trans>
                  </FormHelperText>
                ) : null}
              </CheckboxGroup>
            </FormControl>
          </VStack>

          <Button isLoading={isSubmitting} type="submit" width={'auto'} name={'verify'}>
            {t`Verify`}
          </Button>
        </VStack>
      </form>
    </>
  );
}
